import { ArrowRight, TrendingUp, Award, Boxes, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useLocalBusinessSchema } from '@/hooks/useLocalBusinessSchema';
import { COMPANY } from '@/config/company';
import './Home.css';

const SLIDER_IMAGES = [
  '/background%20slider/0.16724490966079741.webp',
  '/background%20slider/0.995963119027615.webp',
  '/background%20slider/ARKBLACK.QUA.TR.jpg.jpg',
  '/background%20slider/YENiKOLEKSiYON.QUA.CITY.jpg.jpg',
  '/background%20slider/armaturler-ana-sayfa-banner.webp',
  '/background%20slider/creavit-colorit-renkli-banyo-mobilyalari-banner.webp',
  '/background%20slider/slider-3(1).jpg',
];

const HERO_TILES = [
  { label: 'Vitrifiye', sub: 'Creavit', color: '#1a1a2e', count: '889+', path: '/urunler' },
  { label: 'Porselen Karo', sub: 'QUA Granite', color: '#795548', count: '430+', path: '/qua' },
  { label: 'Seramik', sub: 'Duratiles', color: '#7B5E3A', count: '2.200+', path: '/duratiles' },
  { label: 'Yapı Kimyasalları', sub: 'Kalekim', color: '#c0392b', count: '115+', path: '/kalekim' },
  { label: 'Yapıştırıcı', sub: 'Vitra Fix', color: '#2c5f2e', count: '42+', path: '/vitrafix' },
];

const STATS = (yearsActive: number) => [
  { value: 3570, suffix: '+', label: 'Ürün çeşidi', icon: Boxes },
  { value: yearsActive, suffix: ' yıl', label: 'Sektör deneyimi', icon: Award },
  { value: 5, suffix: '', label: 'Partner marka', icon: TrendingUp },
  { value: 1, suffix: ' bölge', label: 'Güneydoğu Anadolu', icon: MapPin },
];

const BRANDS = [
  { logoSrc: '/markalogolar/creavit.svg', name: 'Creavit', path: '/urunler' },
  { logoSrc: '/markalogolar/qualogo.svg', name: 'Qua Granite', path: '/qua' },
  { logoSrc: '/markalogolar/kalekim.svg', name: 'Kalekim', path: '/kalekim' },
  { logoSrc: '/markalogolar/duratiles.svg', name: 'Duratiles Akgün', path: '/duratiles' },
  { logoSrc: '/markalogolar/vitrafix.svg', name: 'Vitra Fix', path: '/vitrafix' },
];

function useCounter(target: number, duration = 1600, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf: number;
    const startTime = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(ease * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return count;
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function StatCard({
  value,
  suffix,
  label,
  icon: Icon,
  inView,
}: {
  value: number;
  suffix: string;
  label: string;
  icon: React.ElementType;
  inView: boolean;
}) {
  const count = useCounter(value, 1600, inView);
  return (
    <div className="stat-card">
      <div className="stat-icon"><Icon size={20} /></div>
      <div className="stat-value">{count.toLocaleString('tr-TR')}{suffix}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

export default function Home() {
  useSEO({
    title: 'Mayi Mimarlık — Diyarbakır Seramik, Vitrifiye & Yapı Malzemeleri | Bölge Distribütörü',
    description:
      "Diyarbakır Kayapınar'da Creavit vitrifiye, Duratiles seramik, QUA Granite, Vitrafix ve Kalekim yapı kimyasallarının bölge distribütörü. Çeysa Plaza showroom. Teklif alın: +90 533 329 95 93.",
    keywords: localKeywords(
      'creavit diyarbakır bayi',
      'duratiles diyarbakır',
      'qua granite diyarbakır',
      'kalekim diyarbakır',
      'vitrafix diyarbakır',
      'kayapınar seramik mağazası',
      'diyarbakır banyo showroom'
    ),
    canonical: 'https://mayimimarlik.com/',
  });
  useLocalBusinessSchema();

  const { ref: statsRef, inView: statsInView } = useInView(0.2);
  const { ref: brandsRef, inView: brandsInView } = useInView(0.12);

  const [activeSlide, setActiveSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState<number | null>(null);
  const sliderTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const goToSlide = useCallback((next: number) => {
    setPrevSlide(activeSlide);
    setActiveSlide(next);
  }, [activeSlide]);

  const restartSlider = useCallback(() => {
    if (sliderTimer.current) clearInterval(sliderTimer.current);
    sliderTimer.current = setInterval(() => {
      setActiveSlide((prev) => {
        setPrevSlide(prev);
        return (prev + 1) % SLIDER_IMAGES.length;
      });
    }, 6000);
  }, []);

  useEffect(() => {
    restartSlider();
    return () => {
      if (sliderTimer.current) clearInterval(sliderTimer.current);
    };
  }, [restartSlider]);

  const currentYear = new Date().getFullYear();
  const yearsActive = currentYear - COMPANY.foundingYear;

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero" aria-label="Ana tanıtım">
        <div className="hero-slider" aria-hidden="true">
          {SLIDER_IMAGES.map((src, i) => (
            <div
              key={src}
              className={`hero-slide${i === activeSlide ? ' hero-slide--active' : ''}${i === prevSlide ? ' hero-slide--prev' : ''}`}
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
          <div className="hero-slider-overlay" />
        </div>

        <div className="hero-slider-dots" role="tablist" aria-label="Slayt seçimi">
          {SLIDER_IMAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === activeSlide}
              aria-label={`Slayt ${i + 1}`}
              className={`hero-dot${i === activeSlide ? ' hero-dot--active' : ''}`}
              onClick={() => {
                goToSlide(i);
                restartSlider();
              }}
            />
          ))}
        </div>

        <div className="hero-inner">
          <div className="hero-left hero-content">
            <span className="hero-badge animate-fade-in">
              {COMPANY.foundingYear}&apos;den beri · Güneydoğu Anadolu distribütörü
            </span>
            <h1 className="hero-title animate-slide-up">
              Seramik, vitrifiye ve
              <span className="hero-title-accent"> yapı malzemeleri</span>
            </h1>
            <p className="hero-subtitle animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Creavit, Qua Granite, Duratiles, Kalekim ve Vitra Fix ürün gruplarında
              {yearsActive} yılı aşkın tecrübemizle projelerinize ürün, stok ve teknik destek sağlıyoruz.
            </p>
            <div className="hero-actions animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/markalar" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Markaları İncele <ArrowRight size={18} />
              </Link>
              <Link to="/iletisim" className="btn btn-outline">
                Teklif Al
              </Link>
            </div>

            <div className="hero-mobile-strip">
              {HERO_TILES.map((item) => (
                <Link key={item.label} to={item.path} className="hero-mobile-chip">
                  <strong>{item.label}</strong>
                  <span>{item.sub}</span>
                  <em>{item.count}</em>
                </Link>
              ))}
            </div>

            <div className="hero-brands animate-fade-in" style={{ animationDelay: '0.35s' }}>
              <span className="hero-brands-label">Distribütörlük</span>
              {['Creavit', 'QUA', 'Duratiles', 'Kalekim', 'Vitra Fix'].map((b) => (
                <span key={b} className="hero-brand-chip">{b}</span>
              ))}
            </div>
          </div>

          <div className="hero-right animate-fade-in" style={{ animationDelay: '0.25s' }}>
            <div className="hero-visual-panel">
              <div className="hero-tile-grid">
                {HERO_TILES.map((item) => (
                  <Link key={item.label} to={item.path} className="hero-tile">
                    <div className="hero-tile-dot" style={{ background: item.color }} />
                    <div className="hero-tile-label">{item.label}</div>
                    <div className="hero-tile-sub">{item.sub}</div>
                    <div className="hero-tile-count">{item.count}</div>
                  </Link>
                ))}
              </div>
              <div className="hero-float-badge">
                <Award size={20} style={{ color: 'var(--accent-gold)', flexShrink: 0 }} />
                <div>
                  <div className="hero-float-val">3.570+</div>
                  <div className="hero-float-label">Katalog ürünü</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-section" ref={statsRef} aria-label="Kurumsal rakamlar">
        <div className="container">
          <div className="stats-grid">
            {STATS(yearsActive).map((s) => (
              <StatCard key={s.label} {...s} inView={statsInView} />
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section
        className={`brands-section section-padding${brandsInView ? ' reveal-in' : ' reveal-hidden'}`}
        ref={brandsRef}
        id="markalar"
      >
        <div className="container">
          <div className="text-center mb-3xl">
            <h2 className="section-title">Partner markalar</h2>
            <div className="section-divider mx-auto" />
            <p className="section-lead">
              Her marka için ayrı katalog, filtre ve online teklif altyapısı.
            </p>
          </div>

          <div className="brands-grid">
            {BRANDS.map((b) => (
              <Link key={b.name} to={b.path} className="brand-card">
                <div className="home-brand-logo-wrap">
                  <img src={b.logoSrc} alt={b.name} className="home-brand-logo-img" loading="lazy" />
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-3xl">
            <Link to="/markalar" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              Marka detayları <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section section-padding">
        <div className="container">
          <div className="cta-card">
            <h2 className="section-title">Showroom&apos;da görün, teklif alın</h2>
            <p className="section-lead">
              Kayapınar&apos;daki showroomumuzda ürünleri yerinde inceleyebilir;
              projeniz için hızlı fiyat teklifi oluşturabilirsiniz.
            </p>
            <div className="cta-actions">
              <Link to="/markalar" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Kataloglara git <ArrowRight size={18} />
              </Link>
              <Link to="/iletisim" className="btn btn-outline">
                İletişim
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
